import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Check, X, Ban, RotateCcw, Trash2, ShieldCheck, Mail, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "", label: "Tous" },
  { key: "pending_approval", label: "En attente" },
  { key: "active", label: "Actifs" },
  { key: "suspended", label: "Suspendus" },
  { key: "rejected", label: "Refusés" },
];
const STATUS_STYLE = {
  active: "bg-emerald-500/15 text-emerald-400",
  pending_approval: "bg-amber-500/15 text-amber-400",
  suspended: "bg-orange-500/15 text-orange-400",
  rejected: "bg-red-500/15 text-red-400",
};
const STATUS_FR = { active: "Actif", pending_approval: "En attente", suspended: "Suspendu", rejected: "Refusé" };
const ROLES = ["viewer", "contributor", "internal_creator", "admin", "super_admin"];
const ROLE_FR = { viewer: "Viewer", contributor: "Contributor", internal_creator: "Internal Creator", admin: "Admin", super_admin: "Super Admin" };

export default function AdminUsers() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("");
  const isSuper = user?.role === "super_admin";

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users", tab],
    queryFn: async () => (await api.get("/admin/users", { params: tab ? { status: tab } : {} })).data,
  });

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); qc.invalidateQueries({ queryKey: ["admin-users"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const setStatus = (id, status, msg) => act(() => api.post(`/admin/users/${id}/status`, { status }), msg);
  const setRole = (id, role) => act(() => api.post(`/admin/users/${id}/role`, { role }), "Rôle mis à jour");
  const del = (id) => { if (window.confirm("Supprimer cet utilisateur ?")) act(() => api.delete(`/admin/users/${id}`), "Utilisateur supprimé"); };

  const resendEmail = (u) => {
    const kind = u.status === "pending_approval" ? "verification" : "reset";
    const label = kind === "verification" ? "l'e-mail de vérification" : "l'e-mail de réinitialisation";
    if (window.confirm(`Renvoyer ${label} à ${u.email} ?`))
      act(() => api.post(`/admin/users/${u.id}/resend-email`, { kind }), "E-mail renvoyé");
  };
  const correctEmail = (u) => {
    const email = window.prompt("Corriger l'adresse e-mail de l'utilisateur :", u.email);
    if (email && email !== u.email)
      act(() => api.post(`/admin/users/${u.id}/correct-email`, { email }), "Adresse e-mail mise à jour");
  };

  const locked = (u) => u.role === "super_admin" && !isSuper;

  if (isLoading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight">Utilisateurs</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <button key={s.key} onClick={() => setTab(s.key)} data-testid={`users-tab-${s.key || "all"}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${tab === s.key ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Utilisateur</th><th className="px-4 py-3">Profil</th>
              <th className="px-4 py-3">Statut</th><th className="px-4 py-3">Rôle</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {u.role === "super_admin" && <ShieldCheck className="h-4 w-4 text-primary" />}
                    <div>
                      <p className="font-medium">{u.name || `${u.first_name || ""} ${u.last_name || ""}`}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                      {(u.email_delivery_status === "bounced" || u.email_delivery_status === "complained") && (
                        <span data-testid={`bounce-badge-${u.id}`}
                          title={`Type: ${u.last_bounce_type || "—"} · Modèle: ${u.last_bounce_email_kind || "—"} · ${(u.last_bounce_at || "").slice(0, 16).replace("T", " ")}`}
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] text-red-400">
                          <AlertTriangle className="h-3 w-3" /> E-mail non livré
                          {u.last_bounce_email_kind ? ` · ${u.last_bounce_email_kind}` : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{u.profile_type || "—"}{u.company ? ` · ${u.company}` : ""}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs ${STATUS_STYLE[u.status] || "bg-white/10"}`}>{STATUS_FR[u.status] || u.status}</span></td>
                <td className="px-4 py-3">
                  <select value={u.role} disabled={locked(u) || u.id === user?.id} onChange={(e) => setRole(u.id, e.target.value)} data-testid={`user-role-${u.id}`}
                    className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs outline-none disabled:opacity-50">
                    {ROLES.map((r) => <option key={r} value={r} disabled={r === "super_admin" && !isSuper}>{ROLE_FR[r]}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => resendEmail(u)} data-testid={`resend-email-${u.id}`} title="Renvoyer un e-mail" className="rounded-md bg-sky-500/15 p-2 text-sky-400 hover:bg-sky-500/25"><Mail className="h-3.5 w-3.5" /></button>
                    <button onClick={() => correctEmail(u)} data-testid={`correct-email-${u.id}`} title="Corriger l'e-mail" className="rounded-md bg-white/5 p-2 text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                    {u.status === "pending_approval" && (
                      <>
                        <button onClick={() => setStatus(u.id, "active", "Utilisateur approuvé")} data-testid={`approve-${u.id}`} disabled={locked(u)} title="Approuver" className="rounded-md bg-emerald-500/15 p-2 text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-40"><Check className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setStatus(u.id, "rejected", "Utilisateur refusé")} data-testid={`reject-${u.id}`} disabled={locked(u)} title="Rejeter" className="rounded-md bg-red-500/15 p-2 text-red-400 hover:bg-red-500/25 disabled:opacity-40"><X className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                    {u.status === "active" && (
                      <button onClick={() => setStatus(u.id, "suspended", "Utilisateur suspendu")} data-testid={`suspend-${u.id}`} disabled={locked(u)} title="Suspendre" className="rounded-md bg-orange-500/15 p-2 text-orange-400 hover:bg-orange-500/25 disabled:opacity-40"><Ban className="h-3.5 w-3.5" /></button>
                    )}
                    {(u.status === "suspended" || u.status === "rejected") && (
                      <button onClick={() => setStatus(u.id, "active", "Utilisateur réactivé")} data-testid={`reactivate-${u.id}`} disabled={locked(u)} title="Réactiver" className="rounded-md bg-emerald-500/15 p-2 text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-40"><RotateCcw className="h-3.5 w-3.5" /></button>
                    )}
                    <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} disabled={u.role === "super_admin" || u.id === user?.id} title="Supprimer" className="rounded-md bg-white/5 p-2 text-muted-foreground hover:text-destructive disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
